import { IonText, IonAvatar, IonBadge, IonSpinner } from '@ionic/react'
import { authorLabelWithNickname, resolveAuthorIdentity } from '../../utils/authorName'
import './Comments.min.css'
import FreeProgramLabel, { isOnFreeProgram } from '../FreeProgramLabel'
import { ImageLoader } from '../ImageLoader'
import { resolveUserAvatarSrc } from '../../services/functions'
import PostFooter from '../Post/PostFooter'
import ImageViewerModal from '../ImageViewerModal'
import { SubscriberYearsMedal } from '../SubscriberYearsMedal'
import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { getCommentAndReplies } from '../../redux/actions'
import { closeCommentsSheet } from '../../redux/reducer/commentsSheetSlice'
import { useProfileNavigation } from '../../utils/profileNavigation'
import { format } from 'timeago.js'

// Render comment text with @nickname mentions highlighted in the primary color.
// Only nicknames the backend confirmed as real mentions (comment.mentions) are
// styled; longest-first so a shorter nickname can't shadow a longer one.
const renderTextWithMentions = (text, mentions, onMentionClick) => {
	const list = (mentions || []).filter((m) => m && m.nickname)
	const nicks = list
		.map((m) => String(m.nickname))
		.sort((a, b) => b.length - a.length)
	if (!nicks.length || !text) return text
	const escaped = nicks.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
	const re = new RegExp(`@(${escaped.join('|')})`, 'g')
	const parts = []
	let last = 0
	let m
	while ((m = re.exec(text)) !== null) {
		// Hoist loop-var reads into per-iteration consts so the closures below
		// don't capture the mutable match (avoids no-loop-func).
		const nick = m[1]
		const matchIndex = m.index
		const matchLen = m[0].length
		if (matchIndex > last) parts.push(text.slice(last, matchIndex))
		// Key the click to the matched mention object (has the user id) so a
		// shared nickname prefix can't link to the wrong profile.
		const mention = list.find((x) => String(x.nickname) === nick)
		const clickable = mention && mention.id != null && onMentionClick
		parts.push(
			<span
				className="comment-mention"
				key={`${matchIndex}-${nick}`}
				role={clickable ? 'button' : undefined}
				onClick={
					clickable
						? (e) => {
								e.stopPropagation()
								onMentionClick(mention)
						  }
						: undefined
				}
			>
				@{nick}
			</span>,
		)
		last = matchIndex + matchLen
	}
	if (last < text.length) parts.push(text.slice(last))
	return parts
}

const Comment = ({
	comment,
	in_post,
	is_reply = false,
	match,
	classes = '',
	show_thread = true,
	// Bottom-sheet drill-in: when provided, opening a comment's replies and the
	// reply button stay inside the sheet instead of navigating to a full page.
	onDrillIn,
	// Flat comments sheet (one-level replies): when provided we drop the drill-in
	// thread screen entirely. Called as onReplyTo(displayComment, parentComment):
	// replies stay flat (always targeting the TOP-LEVEL parent), so replying to a
	// reply adds another sibling reply rather than nesting deeper.
	onReplyTo,
	// For an inline reply, the top-level comment it belongs to (so its own reply
	// button targets the parent, keeping the thread one level deep).
	replyParent = null,
	// Signal carrying a just-posted reply: { parentId, reply, nonce }. When it
	// targets this comment we append the reply to the inline list and expand.
	lastReply,
	// Deep-link focus: scroll/expand to this comment or reply id.
	focusCommentId = null,
	// When set, top-level comments auto-expand their replies (used so a deep-link
	// to a reply renders it, then the sheet scrolls to it).
	expandForFocus = false,
	expandForFocusId = null,
}) => {
	const dispatch = useDispatch()
	const { goToProfile, canOpenProfile } = useProfileNavigation()

	// Opening a profile from inside the comments sheet must also dismiss the
	// sheet so it doesn't stay open in the background. Harmless on the
	// full-page comments view (the sheet is already closed there).
	const openProfile = (user) => {
		dispatch(closeCommentsSheet())
		goToProfile(user)
	}
	const [firstReply, setFirstReply] = useState(null)
	const [loadingReply, setLoadingReply] = useState(false)
	const [isDeleted, setIsDeleted] = useState(false)
	const [imageViewerOpen, setImageViewerOpen] = useState(false)
	const [content, setContent] = useState(comment.content)
	const commentImage = comment.image
		? comment.storage_path + comment.image
		: null
	// Comment clip [24] — same storage as the image, played inline with a plain
	// <video> (comment clips skip the Vimeo pipeline).
	const commentVideo = comment.video
		? comment.storage_path + comment.video
		: null

	const created_by = comment.created_by
	// "Nome Cognome (@nickname)". The three payload shapes — and the fact that
	// subscriber_full_name is already a finished label rather than a nickname —
	// are untangled in one place; reading it as the nickname here is what
	// rendered "Anna V. (@Anna V. (ann…" in the comments.
	const { fullName: authorFull, nickname: authorNick } =
		resolveAuthorIdentity(created_by)
	const authorSfn = (created_by?.subscriber_full_name || '').trim()
	const commentAuthorName = authorLabelWithNickname(created_by) || authorSfn
	const avatarImage = resolveUserAvatarSrc(created_by)
	const alias = created_by?.master_club_subscriber?.alias

	// Age of the comment, shown beside the author instead of in the action row.
	// It used to sit on the far left of that row while Rispondi, the like and the
	// overflow menu were packed together on the right, which is what made Like so
	// easy to miss. Same derivation as PostFooter so the two never disagree.
	const rawCommentDate = comment?.created_at ?? comment?.updated_at
	const parsedCommentDate = rawCommentDate
		? new Date(String(rawCommentDate).replace(' ', 'T'))
		: null
	const commentTimeAgo =
		parsedCommentDate && !Number.isNaN(parsedCommentDate.getTime())
			? format(parsedCommentDate, 'itLocale')
			: ''

	const subscriberInfo = created_by?.master_club_subscriber
	const badge = subscriberInfo?.badge

	const sourceLabel =
		subscriberInfo?.subscription_label ||
		subscriberInfo?.user?.subscription_label ||
		created_by?.subscription_label ||
		created_by?.user?.subscription_label ||
		null

	const sourceSubType =
		subscriberInfo?.subscription?.subscription_type ||
		created_by?.subscription?.subscription_type ||
		null

	// A user "has a badge" when the author payload exposes a badge object
	// (master_club_subscriber.badge) OR the comment author's subscription_label
	// is explicitly a badge (type === 'badge'); a plain subscription is
	// type 'subscription' and must stay unlabelled.
	const hasBadgeOverride = !!badge || sourceLabel?.type === 'badge'

	const rawLabelName = hasBadgeOverride
		? badge?.name || sourceLabel?.name
		: sourceLabel?.name || sourceSubType?.name || 'guest'
	// A badge is always green, as in the feed. A badge-type label carries the
	// CRM hex colour (e.g. "#000000"), which IonBadge's `color` cannot take:
	// the reply's badge rendered with no background at all.
	const rawLabelColor = hasBadgeOverride
		? 'primary'
		: sourceLabel?.color || sourceSubType?.color || 'light'

	const subscriptionLabel = String(rawLabelName || 'guest').toUpperCase()
	const subscriptionColor = rawLabelColor || 'light'

	const yearsAsSubscriber =
		parseInt(subscriberInfo?.days_as_subscriber / 364) || 0
	const hasBadgeRow =
		hasBadgeOverride ||
		isOnFreeProgram(created_by) ||
		!!yearsAsSubscriber

	// "flat sheet" mode = the new one-level comments sheet.
	const flatMode = !!onReplyTo
	const repliesCount = comment.replies_count ?? 0
	const hasReplies = repliesCount > 0
	const shouldShowReplyPreview = !flatMode && in_post && !is_reply && hasReplies

	// ----- Inline (flat) replies, used only in flatMode for top-level comments.
	// Replies are always shown inline (no "Visualizza risposte" tap) — the first
	// page auto-loads as soon as a top-level comment with replies mounts. -----
	const [replies, setReplies] = useState([])
	const [repliesLoading, setRepliesLoading] = useState(false)
	const [repliesPage, setRepliesPage] = useState(0)
	const [repliesHasMore, setRepliesHasMore] = useState(true)
	const repliesAutoLoadedRef = useRef(false)

	const loadReplies = async (page) => {
		setRepliesLoading(true)
		try {
			const res = await dispatch(
				getCommentAndReplies({ commentId: comment.id, page }),
			)
			const data = res.payload?.replies?.data || []
			const lastPage = res.payload?.replies?.last_page ?? page
			setReplies((prev) => {
				const merged = page === 1 ? data : [...prev, ...data]
				// de-dupe by id (a just-posted reply may already be present)
				const seen = new Set()
				return merged.filter((r) => {
					const k = String(r.id)
					if (seen.has(k)) return false
					seen.add(k)
					return true
				})
			})
			setRepliesPage(page)
			setRepliesHasMore(page < lastPage)
		} finally {
			setRepliesLoading(false)
		}
	}

	// Replies are always visible: load the first page as soon as a top-level
	// comment with replies mounts (no expand tap).
	useEffect(() => {
		if (!flatMode || is_reply) return
		if (
			repliesCount > 0 &&
			replies.length === 0 &&
			!repliesAutoLoadedRef.current
		) {
			repliesAutoLoadedRef.current = true
			loadReplies(1)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flatMode, is_reply, repliesCount])

	// Append a just-posted reply to this comment's inline list.
	useEffect(() => {
		if (!flatMode || is_reply) return
		if (!lastReply || String(lastReply.parentId) !== String(comment.id)) return
		setReplies((prev) =>
			prev.some((r) => String(r.id) === String(lastReply.reply.id))
				? prev
				: [...prev, lastReply.reply],
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lastReply?.nonce])

	// Deep-link focus: ensure this top-level comment's replies are loaded so a
	// linked reply renders (the sheet then scrolls to it).
	useEffect(() => {
		if (!flatMode || is_reply || !expandForFocus) return
		// The API names the thread the linked comment sits in. Without that name
		// (older backend), fall back to opening every thread as before.
		if (expandForFocusId && String(comment.id) !== String(expandForFocusId)) return
		if (repliesCount > 0 && replies.length === 0) {
			repliesAutoLoadedRef.current = true
			loadReplies(1)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expandForFocus, expandForFocusId])

	// Prefer using last_reply available from post API to avoid fetching
	useEffect(() => {
		if (shouldShowReplyPreview) {
			if (comment.last_reply) {
				setFirstReply(comment.last_reply)
				return
			}
			if (!firstReply && !loadingReply) {
				setLoadingReply(true)
				dispatch(getCommentAndReplies({ commentId: comment.id, page: 1 }))
					.then((result) => {
						const data = result.payload?.replies?.data || []
						if (data.length > 0) {
							// This preview shows the MOST RECENT reply (it is the
							// fallback for `comment.last_reply`). Replies now arrive
							// oldest-first, so the newest one on the page is the last
							// element — reading data[0] here would show the oldest.
							setFirstReply(data[data.length - 1])
						}
						setLoadingReply(false)
					})
					.catch(() => {
						setLoadingReply(false)
					})
			}
		}
	}, [shouldShowReplyPreview, comment.last_reply, firstReply, loadingReply, dispatch, comment.id])

	const handleReplyPreviewClick = () => {
		// Inside the sheet: drill into the thread in place.
		if (onDrillIn) {
			onDrillIn(comment)
			return
		}
		// Default (full-page): navigate to the complete comments page
		const currentPath = window.location.pathname
		const newPath = `${currentPath}/${comment.id}`
		window.history.pushState(null, '', newPath)
		window.location.href = newPath
	}

	const renderReplyPreview = () => {
		if (!firstReply) return null

		const replyCreatedBy = firstReply.created_by
		const replyFullname = replyCreatedBy?.subscriber_full_name
		const replyAvatarImage = resolveUserAvatarSrc(replyCreatedBy)

		const truncatedContent = firstReply.content?.length > 60
			? firstReply.content.substring(0, 60) + '...'
			: firstReply.content

		const remainingReplies = (comment.replies_count ?? 0) > 1 ? (comment.replies_count - 1) : 0

		return (
			<div className="reply-preview">
				<div className="reply-container" onClick={handleReplyPreviewClick}>
					<div
						className="reply-avatar"
						onClick={(e) => {
							e.stopPropagation()
							openProfile(replyCreatedBy)
						}}
						style={{ cursor: canOpenProfile(replyCreatedBy) ? 'pointer' : 'default' }}
					>
						<IonAvatar>
							<ImageLoader
								imageType="user"
								image={replyAvatarImage}
								fallbackName={replyCreatedBy}
							/>
						</IonAvatar>
					</div>
					<div className="reply-content">
						<div className="reply-bubble">
							<span
								className="reply-author"
								onClick={(e) => {
									e.stopPropagation()
									openProfile(replyCreatedBy)
								}}
								style={{ cursor: canOpenProfile(replyCreatedBy) ? 'pointer' : 'default' }}
							>
								{replyFullname}
							</span>
							<span className="reply-text">{truncatedContent}</span>
						</div>
						<div className="more-replies">
							<IonText color="primary">
								{remainingReplies === 0 ? `Rispondi...` : remainingReplies === 1 ? `Visualizza 1 altra risposta...` : `Visualizza altre ${remainingReplies} risposte...`}
							</IonText>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Flat (one-level) inline replies for the comments sheet.
	const renderInlineReplies = () => {
		if (is_reply) return null
		const showCount = Math.max(repliesCount, replies.length)
		if (showCount === 0) return null

		const showMore = replies.length > 0 && repliesHasMore && !repliesLoading

		return (
			<div className="sheet-replies">
				{replies.map((r) => (
					<Comment
						key={r.id}
						comment={r}
						in_post={true}
						is_reply={true}
						onReplyTo={onReplyTo}
						replyParent={comment}
						lastReply={lastReply}
						focusCommentId={focusCommentId}
					/>
				))}

				{(repliesLoading || showMore) && (
					<div className="sheet-replies-actions">
						{repliesLoading && <IonSpinner name="dots" />}
						{showMore && (
							<button
								type="button"
								className="sheet-replies-toggle"
								onClick={() => loadReplies(repliesPage + 1)}
							>
								Mostra altre risposte
							</button>
						)}
					</div>
				)}
			</div>
		)
	}

	if (isDeleted) return null

	return (
		<div id={`comment-${comment.id}`} className={`comment ${classes} ${is_reply ? 'is-reply' : 'is-main-comment'}`}>
			{/* Visual thread for replies */}
			{is_reply && show_thread && <div className="comment-thread-line"></div>}

			<div className="comment-container">
				{/* Avatar */}
				<div
					className="comment-avatar"
					onClick={() => openProfile(created_by)}
					style={{ cursor: canOpenProfile(created_by) ? 'pointer' : 'default' }}
				>
					<IonAvatar>
						<ImageLoader
							imageType="user"
							image={avatarImage}
							fallbackName={created_by}
						/>
					</IonAvatar>
				</div>

				{/* Visual thread for reply preview */}
				{shouldShowReplyPreview && <div className="reply-thread-line"></div>}

				{/* Comment content */}
				<div className="comment-main-content">
					{/* Rounded box with name and comment */}
					<div className="comment-bubble">
						<div className="comment-header">
							<span
								className="comment-author"
								onClick={() => openProfile(created_by)}
								style={{ cursor: canOpenProfile(created_by) ? 'pointer' : 'default' }}
							>
								{commentAuthorName}
							</span>
						</div>
						{/* Badges and the age share the line under the name. Beside
						    "Nome (@nick)", two badges (e.g. SUPPORTO MOTIVAZIONALE +
						    FREE) squeezed the name and each other; here they wrap
						    whole instead. Without badges it is just the age, as before. */}
						{(hasBadgeRow || commentTimeAgo) && (
							<div className="comment-meta">
								{hasBadgeRow && (
									<div className="comment-badges">
										{/* Status label shown ONLY for users who have a badge, so
										    badge holders can be distinguished within comments
										    (client request). */}
										{hasBadgeOverride && (
											<IonBadge color={subscriptionColor}>
												{subscriptionLabel}
											</IonBadge>
										)}
										{/* Team only: the backend sends is_free_program to badge holders alone. */}
										<FreeProgramLabel user={created_by} />
										{!!yearsAsSubscriber && !hasBadgeOverride && (
											<SubscriberYearsMedal years={yearsAsSubscriber} size="sm" />
										)}
									</div>
								)}
								{commentTimeAgo && (
									<span className="comment-time-ago">{commentTimeAgo}</span>
								)}
							</div>
						)}
						{comment.reply_to_name ? (
							<div className="comment-reply-to">
								In risposta a <strong>{comment.reply_to_name}</strong>
							</div>
						) : null}
						<div className="comment-text">
							{renderTextWithMentions(content, comment.mentions, (mention) => {
								if (canOpenProfile({ id: mention.id }))
									openProfile({ id: mention.id })
							})}
						</div>
						{commentImage && (
							<div className="comment-image-container">
								{/* A button, not a bare <img> with a click handler: the photo
								    is now an action, so it has to be reachable by keyboard
								    and announced as one. */}
								<button
									type="button"
									className="comment-image-button"
									onClick={() => setImageViewerOpen(true)}
									aria-label="Apri la foto a schermo intero"
								>
									<ImageLoader
										image={commentImage}
										css="comment-image"
									/>
								</button>
							</div>
						)}
						{commentImage && (
							<ImageViewerModal
								isOpen={imageViewerOpen}
								src={commentImage}
								onClose={() => setImageViewerOpen(false)}
							/>
						)}
						{commentVideo && (
							<div className="comment-image-container">
								<video
									className="comment-video"
									src={commentVideo}
									controls
									playsInline
									preload="metadata"
								/>
							</div>
						)}
					</div>

					{/* Footer with action buttons */}
					<div className="comment-actions">
						<PostFooter
							comment={comment}
							is_reply={is_reply}
							match={match}
							classes="comment-footer"
							/* Rendered in the header now, beside the author. */
							date={false}
							likes={true}
							comments={flatMode ? true : !is_reply}
							reports={true}
							onDelete={() => setIsDeleted(true)}
							onEdit={(newContent) => setContent(newContent)}
							onCommentReply={
								onReplyTo
									? // Replying to a reply still targets the TOP-LEVEL
									  // parent so the thread stays one level deep.
									  () => onReplyTo(comment, is_reply ? replyParent : comment)
									: onDrillIn && !is_reply
										? () => onDrillIn(comment)
										: undefined
							}
						/>
					</div>

					{/* Replies: flat inline list (sheet) or single preview (full-page) */}
					{flatMode
						? renderInlineReplies()
						: shouldShowReplyPreview && renderReplyPreview()}
				</div>
			</div>
		</div>
	)
}

export default Comment
